import React from 'react';
import clsx from 'clsx';
import {
  AspectRatio,
  MediaQuery,
  MediaQueryPictureOrder,
  MediaQueryStyleOrder,
  buildStaticImageRenditions,
  generateImageRenditions,
  getStaticImageSrc,
  sortRenditions,
} from '../../utils/ImageUtils';
import * as Styled from './AImage.styles';
import { type AImageProps } from './AImage.types';

const COMPONENT_NAME = 'a-web-ui-kit-AImage';
const TEST_ID = 'web-ui-kit-AImage';

export function AImage(props: AImageProps): React.ReactElement {
  const {
    imgClass,
    title,
    image,
    loading = 'lazy',
    renditions = [],
    src,
    alt,
    onError,
    fetchpriority,
    dynamic: _dynamic, // Prefix with underscore to indicate intentionally unused
    ...restProps
  } = props;

  const effectiveAlt = image?.altText ?? alt ?? '';

  const isStaticImage = image?.dynamic !== undefined && !image.dynamic;
  const marriottRenditions = image?.marriottRenditions;

  const useMarriottRenditions =
    isStaticImage &&
    !!marriottRenditions &&
    Object.values(marriottRenditions).some(url => typeof url === 'string' && url.trim());

  const effectiveRenditions = useMarriottRenditions
    ? buildStaticImageRenditions(marriottRenditions, renditions)
    : isStaticImage
    ? []
    : renditions;

  const marriottSrc = useMarriottRenditions ? getStaticImageSrc(marriottRenditions) : '';
  const effectiveSrc = marriottSrc.trim() || image?.assetPath?.trim() || src?.trim() || '';

  // ensure renditions are sorted in correct order for picture source elements
  // and style definitions, respectively -- the order is very significant
  const pictureRenditions = sortRenditions(effectiveRenditions, MediaQueryPictureOrder);
  const styleRenditions = sortRenditions(effectiveRenditions, MediaQueryStyleOrder);

  return (
    <Styled.AImagePicture
      renditions={styleRenditions}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      {...restProps}
    >
      {pictureRenditions.map((rendition, index: number) => {
        const { renditionPath, media } = rendition;
        return (
          <source
            key={index}
            media={media}
            srcSet={renditionPath}
            data-srcset={renditionPath}
            data-testid={`${TEST_ID}-source-img-${index}`}
          />
        );
      })}

      <img
        src={effectiveSrc}
        className={clsx('image', loading, imgClass)}
        alt={effectiveAlt}
        data-src={effectiveSrc}
        loading={loading}
        title={title}
        onError={onError}
        {...(fetchpriority && { fetchpriority })}
      />
    </Styled.AImagePicture>
  );
}

export {
  AspectRatio as AImageAspectRatio,
  MediaQuery as AImageMediaQuery,
  generateImageRenditions,
  TEST_ID as A_IMAGE_TEST_ID,
};
